import { apiFetch } from "./apiHelper";
import { API_BASE } from "./apiHelper";
import { timezoneUtils } from "../utils/timezone";
import { devError, devWarn } from "../utils/devConsole";

// Recupera tutte le pianificazioni
export const getPianificazioni = async () => {
  try {
    return await apiFetch("/pianificazioni");
  } catch (error) {
    devError("❌ Error while fetching the pianificazioni:", error.message);
    throw error;
  }
};

// Aggiungi una nuova pianificazione
export const addPianificazione = async (pianificazione) => {
  try {
    return await apiFetch("/pianificazioni", "POST", pianificazione);
  } catch (error) {
    devError("❌ Error while adding the pianificazione:", error.message);
    throw error;
  }
};

// Aggiorna una pianificazione
export const updatePianificazione = async (pianificazione_id, updatedData) => {
  try {
    return await apiFetch(`/pianificazioni/${pianificazione_id}`, "PUT", updatedData);
  } catch (error) {
    devError("❌ Error while updating the pianificazione:", error.message);
    throw error;
  }
};

// Rimuovi una pianificazione
export const deletePianificazione = async (pianificazione_id) => {
  try {
    return await apiFetch(`/pianificazioni/${pianificazione_id}`, "DELETE");
  } catch (error) {
    devError("❌ Error while deleting the pianificazione:", error.message);
    throw error;
  }
};

// Recupera la pianificazione attiva
export const getActivePianificazione = async () => {
  try {
    const data = await apiFetch("/pianificazioni");
    const today = timezoneUtils.getTodayISO();
    return data.find(plan => today >= plan.DataInizio && today <= plan.DataFine);
  } catch (error) {
    devError("❌ Error while fetching the active pianificazione:", error.message);
    throw error;
  }
};

// Tutte le pianificazioni attive oggi (date sovrapposte incluse), ordinate per Numero
export const getActivePianificazioni = async () => {
  try {
    const data = await apiFetch("/pianificazioni");
    const today = timezoneUtils.getTodayISO();
    return data
      .filter((plan) => today >= plan.DataInizio && today <= plan.DataFine)
      .sort((a, b) => (a.Numero ?? 0) - (b.Numero ?? 0));
  } catch (error) {
    devError("❌ Error while fetching active pianificazioni:", error.message);
    throw error;
  }
};

// Recupera le immagini di una pianificazione
export const getImagesByPianificazione = async (pianificazione_id) => {
  try {
    const response = await apiFetch(`/immagini/${pianificazione_id}`);
    return response;
  } catch (error) {
    devError("❌ Error while fetching the images:", error.message);
    throw error;
  }
};

// Aggiungi un'immagine a una pianificazione
export const addImageByPlan = async (formData) => {
  const idPlan = formData.get("IdPianificazione");

  try {
    const url = `${API_BASE}/immagini/${idPlan}`;
    const response = await fetch(url, {
      method: "POST",
      body: formData,
      credentials: 'include',
    });

    const contentType = response.headers.get("Content-Type");

    if (!response.ok) {
      if (response.status === 413) {
        throw new Error("L'immagine è troppo grande. Dimensione massima consentita: 50MB");
      }
      if (response.status === 415) {
        throw new Error("Formato immagine non supportato. Usa JPG, PNG o GIF");
      }
      throw new Error(`Errore durante l'upload: ${response.status}`);
    }

    if (!contentType?.includes("application/json")) {
      const raw = await response.text();
      devError("⚠️ Non-JSON response received:", raw);
      throw new Error("La risposta del server non è un JSON!");
    }

    const jsonResponse = await response.json();
    return jsonResponse;

  } catch (error) {
    devError("❌ Error while sending the image:", error.message);
    throw error;
  }
};

// Aggiorna un'immagine
export const updateImage = async (imageId, updatedData) => {
  try {
    return await apiFetch(`/immagini/${imageId}`, "PUT", updatedData);
  } catch (error) {
    devError("❌ Error while updating the image:", error.message);
    throw error;
  }
};

// Rimuovi un'immagine
export const deleteImage = async (imageId) => {
  try {
    return await apiFetch(`/immagini/${imageId}`, "DELETE");
  } catch (error) {
    devError("❌ Error while deleting the image:", error.message);
    throw error;
  }
};

export const loginWithMicrosoft = async (accessToken) => {
  try {
    const response = await fetch(`${API_BASE}/auth/microsoft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accessToken }),
    });
    
    if (!response.ok) throw new Error('Microsoft login failed');
    return await response.json();
  } catch (error) {
    throw new Error(`Authentication error: ${error.message}`);
  }
};

export const logoutFromBackend = async (token) => {
  try {
    const response = await fetch(`${API_BASE}/auth/logout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      throw new Error(`Errore durante il logout: ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    devError("Errore durante il logout dal backend:", error)
    throw error
  }
}

// Aggiungi un banner
export const addBanner = async (bannerData) => {
  try {
    const response = await apiFetch("/banner", "POST", bannerData);
    return response;
  } catch (error) {
    devError("❌ Error while adding banner:", error.message);
    throw error;
  }
};

// Aggiorna un banner
export const updateBanner = async (banner_id, updatedData) => {
  try {
    const response = await apiFetch(`/banner/${banner_id}`, "PUT", updatedData);
    return response;
  } catch (error) {
    devError("❌ Error while updating banner:", error.message);
    throw error;
  }
};

// Elimina un banner
export const deleteBanner = async (id) => {
  try {
    return await apiFetch(`/banner/${id}`, "DELETE");
  } catch (error) {
    devError("❌ Error while deleting banner:", error.message);
    throw error;
  }
};

// Recupera tutti i banner
export const getBanner = async () => {
  try {
    return await apiFetch("/banner");
  } catch (error) {
    devError("❌ Errore durante il recupero dei banner:", error.message);
    throw error;
  }
};

export const getActiveBanner = async () => {
  try {
    // Recupera tutti i banner
    const data = await apiFetch("/banner");
    const today = timezoneUtils.getTodayISO();
    
    const activeBanner = data.find(plan => {
      return today >= plan.DataInizio && today <= plan.DataFine;
    });
    
    return activeBanner;
  } catch (error) {
    devError("❌ Error while fetching the active banner:", error.message);
    throw error;
  }
};

/** Tutti i banner attivi oggi (date sovrapposte incluse), ordinati per Numero */
export const getActiveBanners = async () => {
  try {
    const data = await apiFetch("/banner");
    const today = timezoneUtils.getTodayISO();
    return data
      .filter((b) => today >= b.DataInizio && today <= b.DataFine)
      .sort((a, b) => (a.Numero ?? 0) - (b.Numero ?? 0));
  } catch (error) {
    devError("❌ Error while fetching active banners:", error.message);
    throw error;
  }
};

export const addImageByBanner = async (formData) => {
  const idBan = formData.get("IdBanner");
  try {
    const response = await fetch(`${API_BASE}/immagini/banner/${idBan}`, {
      method: "POST",
      body: formData,
    });
    return await response.json();
  } catch (error) {
    devError("❌ Error adding banner image:", error.message);
    throw error;
  }
};

export const getImagesByBanner = async (banner_id) => {
  try {
    const response = await apiFetch(`/immagini/banner/${banner_id}`);
    return response;
  } catch (error) {
    devError("❌ Error fetching banner images:", error.message);
    throw error;
  }
};


// Recupera la configurazione di sessione dal backend
export const getSessionConfig = async () => {
  try {
    return await apiFetch("/config/session", "GET");
  } catch (error) {
    devWarn("Impossibile recuperare la configurazione di sessione, utilizzo del valore predefinito");
    return { sessionDurationMinutes: 60 };
  }
};